import { useLazyQuery, useMutation } from '@apollo/client'
import { useAuth0 } from '@auth0/auth0-react'
import { CreateIdentityDocument, FetchMyIdentityDocument } from '@salescore/client-api'
import { isUserAccountAlreadyExistsError } from '@salescore/client-base'
import { Loading } from '@salescore/client-common'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Button, Card } from 'antd'
import { type ReactNode, Suspense, useEffect, useRef } from 'react'
import type { FallbackProps } from 'react-error-boundary'
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary'

import { AuthenticatedApolloProvider } from '../providers/AuthenticatedApolloProvider'

const NoInvitations = (): ReactNode => (
  <>
    <p>あなたのアカウントは招待を受けていません。貴社の管理者にアカウントの招待を依頼してください。</p>
    <p>
      突然ログインできなくなった場合はログイン時の認証方法を間違えている可能性があります
      (例えばSalesforceログインを指定されているのにGoogleアカウントを利用したなど)。
      ログイン時の認証サービスを確認の上もう一度ログインを試してください。
    </p>
    <p>それでも解決しない場合は弊社営業担当者までお問い合わせください。</p>
  </>
)

const OrganizationNotFound = (): ReactNode => (
  <>
    <p>このアカウントが所属する組織はありません。 組織設定がまだの方は、弊社営業担当者までお問い合わせください。</p>
    <p>
      既に組織に所属している場合は、違うアカウントで再度ログインを試すか、
      貴社の管理者にアカウントの招待を依頼してください。
    </p>
  </>
)

const EmailNotVerified = (): ReactNode => <>メールアドレスが認証されていません。</>

const AlreadyExists = (): ReactNode => <>既に新規登録済みのアカウントです。</>

const GeneralErrorMessage = ({ message }: { message?: string }): ReactNode => (
  <>
    <p>エラーが発生しました。エラーが解消されない場合、右下のチャットボタンよりお問い合わせください。</p>
    <p>メッセージ: {message ?? '不明なエラー'}</p>
  </>
)

const ErrorBody = ({ error }: { error: Error }): ReactNode => {
  if (error.message.startsWith('NoInvitations')) {
    return <NoInvitations />
  } else if (error.message.startsWith('NotFound')) {
    return <OrganizationNotFound />
  } else if (isUserAccountAlreadyExistsError(error)) {
    // Identity は存在するが myIdentity が JWT と紐づかず / へ誘導できないケース (SignUp 参照)
    return <AlreadyExists />
  } else if (error.message.includes(`メールアドレス認証が完了していません`)) {
    return <EmailNotVerified />
  } else {
    return <GeneralErrorMessage message={error.message} />
  }
}

const FallbackComponent = ({ error }: FallbackProps): ReactNode => {
  const { user } = useAuth0()

  return (
    <div className="flex size-full flex-col items-center justify-center align-middle text-gray-400">
      <Card className="w-[600px]">
        {error instanceof Error ? <ErrorBody error={error} /> : <GeneralErrorMessage />}
        {user !== undefined && (
          <ul>
            <li>メールアドレス: {user.email}</li>
            <li>認証ID: {user.sub}</li>
          </ul>
        )}
        <div className="mt-4 flex flex-col items-center justify-center">
          <Link to="/sign_out">
            <Button type="primary">ログインページへ</Button>
          </Link>
        </div>
      </Card>
    </div>
  )
}

const SignUp = (): ReactNode => {
  const [createUserAccountMutation] = useMutation(CreateIdentityDocument)
  const [fetchMyIdentity] = useLazyQuery(FetchMyIdentityDocument, { fetchPolicy: 'no-cache' })
  const { showBoundary } = useErrorBoundary()
  const navigate = useNavigate()
  const hasRequestedCreationReference = useRef(false)

  useEffect(() => {
    // useMutation の state 更新による再レンダーで createIdentity が並行して多重発火し、
    // 2本目以降が AlreadyExists になる不具合があったため、発火は初回の1回に制限する (issue #9463)
    if (hasRequestedCreationReference.current) {
      return
    }
    hasRequestedCreationReference.current = true

    const createIdentityAndRedirect = async (): Promise<void> => {
      try {
        await createUserAccountMutation()
        await navigate({ to: '/' })
      } catch (error) {
        if (!isUserAccountAlreadyExistsError(error)) {
          showBoundary(error)
          return
        }
        // identity が既に存在するなら新規登録は不要なので / へ誘導する。
        // ただし myIdentity が取得できない状態 (identity はあるが JWT と紐づかない等) で誘導すると
        // / ↔ /sign_up のリダイレクトループになるため、取得できたときのみ誘導する
        try {
          const { data, error: fetchMyIdentityError } = await fetchMyIdentity()
          if (fetchMyIdentityError !== undefined || data?.myIdentity === null || data?.myIdentity === undefined) {
            showBoundary(error)
            return
          }
          await navigate({ to: '/' })
        } catch {
          showBoundary(error)
        }
      }
    }
    void createIdentityAndRedirect()
  }, [createUserAccountMutation, fetchMyIdentity, navigate, showBoundary])
  return <></>
}

const Page = (): ReactNode => (
  <AuthenticatedApolloProvider>
    <ErrorBoundary FallbackComponent={FallbackComponent}>
      <Suspense fallback={<Loading message="新規登録中..." />}>
        <SignUp />
      </Suspense>
    </ErrorBoundary>
  </AuthenticatedApolloProvider>
)

export const Route = createFileRoute('/sign_up')({
  component: Page,
})
